/**
 * Traffic for a small local plumber: a few dozen visitors a day, weekday-heavy,
 * mostly from Google. Generated from a fixed formula rather than random numbers
 * so the chart looks the same on every load and the totals always agree with
 * the series under them.
 */

import { dayKey } from './time';

export interface DemoTimeseriesPoint {
  date: string;
  visitors: number;
  pageviews: number;
}

export interface DemoAnalytics {
  overview: {
    visitors: number;
    pageViews: number;
    bounceRate: number;
    averageSessionDuration: number;
  };
  visitors: {
    today: number;
    yesterday: number;
    thisWeek: number;
    thisMonth: number;
    change: string;
  };
  topPages: Array<{ path: string; views: number; title: string }>;
  sources: Array<{ name: string; visitors: number; percent: number }>;
  devices: Array<{ name: string; visitors: number; percent: number }>;
  browsers: Array<{ name: string; visitors: number; percent: number }>;
  countries: Array<{ name: string; visitors: number; percent: number }>;
}

/** Visitors for the day `offset` days back — weekdays busier, weekends quiet. */
function visitorsFor(offset: number): number {
  const day = new Date(Date.now() - offset * 86_400_000).getDay();
  const weekend = day === 0 || day === 6;
  const base = weekend ? 14 : 31;
  // A gentle, repeating wobble — enough shape to read as real traffic.
  const wobble = [0, 4, -3, 6, -2, 3, -5, 2][offset % 8];
  return Math.max(6, base + wobble);
}

export function demoTimeseries(days: number): DemoTimeseriesPoint[] {
  const points: DemoTimeseriesPoint[] = [];
  for (let offset = days - 1; offset >= 0; offset--) {
    const visitors = visitorsFor(offset);
    points.push({
      date: dayKey(-offset),
      visitors,
      pageviews: Math.round(visitors * 2.4),
    });
  }
  return points;
}

export function demoAnalytics(days: number): DemoAnalytics {
  const series = demoTimeseries(days);
  const visitors = series.reduce((sum, point) => sum + point.visitors, 0);
  const pageViews = series.reduce((sum, point) => sum + point.pageviews, 0);
  const last7 = series.slice(-7).reduce((sum, point) => sum + point.visitors, 0);
  const last30 = series.slice(-30).reduce((sum, point) => sum + point.visitors, 0);

  const share = (percent: number) => Math.round((visitors * percent) / 100);

  return {
    overview: {
      visitors,
      pageViews,
      bounceRate: 46,
      averageSessionDuration: 97,
    },
    visitors: {
      today: series[series.length - 1]?.visitors ?? 0,
      yesterday: series[series.length - 2]?.visitors ?? 0,
      thisWeek: last7,
      thisMonth: last30,
      change: '+12%',
    },
    topPages: [
      { path: '/', views: Math.round(pageViews * 0.42), title: 'Republic Plumbing — Madison, WI' },
      { path: '/services/water-heaters', views: Math.round(pageViews * 0.16), title: 'Water heater repair & replacement' },
      { path: '/services/drain-cleaning', views: Math.round(pageViews * 0.13), title: 'Drain cleaning' },
      { path: '/contact', views: Math.round(pageViews * 0.11), title: 'Contact' },
      { path: '/emergency', views: Math.round(pageViews * 0.09), title: '24-hour emergency plumbing' },
      { path: '/areas/middleton', views: Math.round(pageViews * 0.05), title: 'Plumber in Middleton' },
    ],
    sources: [
      { name: 'google.com', visitors: share(58), percent: 58 },
      { name: 'Direct', visitors: share(24), percent: 24 },
      { name: 'facebook.com', visitors: share(9), percent: 9 },
      { name: 'yelp.com', visitors: share(6), percent: 6 },
      { name: 'nextdoor.com', visitors: share(3), percent: 3 },
    ],
    devices: [
      { name: 'Mobile', visitors: share(68), percent: 68 },
      { name: 'Desktop', visitors: share(27), percent: 27 },
      { name: 'Tablet', visitors: share(5), percent: 5 },
    ],
    browsers: [
      { name: 'Chrome', visitors: share(46), percent: 46 },
      { name: 'Safari', visitors: share(39), percent: 39 },
      { name: 'Edge', visitors: share(9), percent: 9 },
      { name: 'Firefox', visitors: share(6), percent: 6 },
    ],
    countries: [{ name: 'United States', visitors, percent: 100 }],
  };
}

export function demoPeriodDays(period: string | null): number {
  if (period === '7d') return 7;
  if (period === '90d') return 90;
  return 30;
}
